import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { catchError, finalize, forkJoin, of, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { SubService } from 'src/app/services/sub.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { BeControleFinanca } from 'src/app/models/BeControleFinanca';
import { BeControleFinancaXMovimento } from 'src/app/models/BeControleFinancaXMovimento';
import { MatDialog } from '@angular/material/dialog';
import { DialogTextValueComponent } from 'src/app/components/dialog-text-value/dialog-text-value.component';
import { formatDate } from '@angular/common';
import { BeControleFinancaMovimento } from '@app/models/BeControleFinancaMovimento';
import { ListControleFinancaMovimentosComponent } from '../list-movimentos/list-controle-financa-movimentos.component';

@Component({
  selector: 'app-edit-controle-financas',
  templateUrl: './edit-controle-financas.component.html',
  styleUrls: ['./edit-controle-financas.component.css']
})
export class EditControleFinancasComponent implements OnInit, OnDestroy {
  @ViewChildren(ListControleFinancaMovimentosComponent) childComponents!: QueryList<ListControleFinancaMovimentosComponent>;

  public listaControleFinancaXMovimento: BeControleFinancaXMovimento[] = [];
  public controleFinancaXMovimentoEntrada?: BeControleFinancaXMovimento[] = [];
  public controleFinancaXMovimentoSaida?: BeControleFinancaXMovimento[] = [];
  public listaControleFinancaXMovimentoCartoes: BeControleFinancaXMovimento[] = [];
  public vlEntrada?: number;
  public vlSaida?: number;
  public vlCartao: number = 0;
  public vlAcumuladoMesAnterior?: number;
  public blMesFinalizado?: boolean;
  public vlSaldoAcumulado?: number;
  public vlSaldoAtual?: number;
  public idControleFinancaAnterior?: number;
  public idControleFinancaProximo?: number;
  public blMesAnteriorFinalizado?: boolean;
  public blMesProximoFinalizado?: boolean;
  public editFormGroup: FormGroup;
  public subscription?: Subscription;
  public vlTotal: Map<number, number> = new Map<number, number>();
  public vlPago: Map<number, number> = new Map<number, number>();
  public iconColor?: string;
  public selectedIndex: number = 0;
  public movimentosCount: Map<number, number> = new Map<number, number>();

  private readonly brlFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  private editIdControleFinancaFormControl = new FormControl<number | null>(null, [Validators.required]);
  private editIdUsuarioFormControl = new FormControl<number | null>(null, [Validators.required]);
  private editDtControleFormControl = new FormControl<Date | null>(null, [Validators.required]);

  public constructor(
    private httpClient: HttpClient,
    private route: ActivatedRoute,
    private sub: SubService,
    private dialog: MatDialog,
    private router: Router,
    private snackBarService: SnackBarService,
  ) {
    this.editFormGroup = new FormGroup({
      idControleFinanca: this.editIdControleFinancaFormControl,
      idUsuario: this.editIdUsuarioFormControl,
      dtControle: this.editDtControleFormControl,
    });
  }

  public ngOnInit(): void {
    this.sub.clear();
    this.onCarregarCampos();
  }

  public ngOnDestroy(): void {
    this.sub.clear();
    this.unsubscribe();
  }

  public onSalvar() {
    this.unsubscribe();
    this.subscription = this.httpClient.put<unknown>(
      `/api/ControleFinancas`, this.editFormGroup.value
    ).subscribe({
      next: () => {
        this.unsubscribe();
        this.snackBarService.exibirSucesso('Registro salvo!');
        this.router.navigate(['/controle-financas'], { relativeTo: this.route });
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
      }
    });
  }

  public onAdicionarCartao() {
    if (this.subscription)
      return;
    this.dialog.open(DialogTextValueComponent, {
      data: {
        title: 'Adicionar Cartão',
        textName: 'noTipoMovimento',
        textTitle: 'Nome do Cartão',
        numberName: 'vlSaldo',
        numberTitle: 'Saldo do Cartão',
        numberRequired: false,
        buttonText: 'Adicionar',
        idTabelaPai: 'idControleFinanca',
        idTabela: this.editIdControleFinancaFormControl.value!
      }
    }).afterClosed().subscribe((result) => {
      this.unsubscribe();
      if (result) {
        this.subscription = this.httpClient.post<BeControleFinancaXMovimento>('/api/ControleFinancaXMovimentos', result).subscribe({
          next: (res) => {
            this.snackBarService.exibirSucesso('Registro salvo!');
            this.listaControleFinancaXMovimento.push(res);
            this.listaControleFinancaXMovimentoCartoes.push(res);
            this.onCalcularValorTotal();
          },
          error: (err) => {
            this.snackBarService.exibirErro(`Erro! - ${err.statusText}/${err.status} - ${err.error.message}`);
            this.unsubscribe();
          }
        });
      }
    })
  }

  public onEncerrarMes() {
    if (this.subscription)
      return;

    const idControleFinanca = this.editIdControleFinancaFormControl.value!;
    this.subscription = this.httpClient.put(`/api/ControleFinancas/EncerrarMes/${idControleFinanca}`, {}).subscribe({
      next: (response: any) => {
        this.snackBarService.exibirSucesso(response.mensagem || 'Mês encerrado com sucesso!');
        this.onCarregarCampos();
      },
      error: (err) => {
        const mensagem = err.error?.mensagem || `Erro! - ${err.statusText}/${err.status}`;
        this.snackBarService.exibirErro(mensagem);
        this.unsubscribe();
      }
    });
  }

  public onReabrirMes() {
    if (this.subscription)
      return;

    const idControleFinanca = this.editIdControleFinancaFormControl.value!;
    this.subscription = this.httpClient.put(`/api/ControleFinancas/ReabrirMes/${idControleFinanca}`, {}).subscribe({
      next: (response: any) => {
        this.snackBarService.exibirSucesso(response.mensagem || 'Mês reaberto com sucesso!');
        this.onCarregarCampos();
      },
      error: (err) => {
        const mensagem = err.error?.mensagem || `Erro! - ${err.statusText}/${err.status}`;
        this.snackBarService.exibirErro(mensagem);
        this.unsubscribe();
      }
    });
  }

  public getFormattedDate(): string {
    const dtControle = this.editFormGroup.get('dtControle')?.value;
    if (dtControle) {
      const formattedDate = formatDate(dtControle, 'MMMM yyyy', 'pt-br');
      return formattedDate;
    }
    return '';
  }

  public temOpcoesDisponiveis(): boolean {
    const podeEncerrar = !this.blMesFinalizado &&
      (this.blMesAnteriorFinalizado === true || this.blMesAnteriorFinalizado === null) &&
      (this.blMesProximoFinalizado === false || this.blMesProximoFinalizado === null);

    const podeReabrir = this.blMesFinalizado &&
      (this.blMesProximoFinalizado === false || this.blMesProximoFinalizado === null) &&
      (this.blMesAnteriorFinalizado === true || this.blMesAnteriorFinalizado === null);

    return !!(podeEncerrar || podeReabrir);
  }

  public formatBRL(value?: number | null): string {
    const normalizedValue = Math.abs(value ?? 0) < 0.005 ? 0 : value ?? 0;
    const formatted = this.brlFormatter.format(normalizedValue);
    return formatted.replace(/\s|\u00A0/g, '');
  }

  public getResumoTotal(idCofx: number): number {
    return this.vlTotal.get(idCofx) ?? 0;
  }

  public getResumoPago(idCofx: number): number {
    return this.vlPago.get(idCofx) ?? 0;
  }

  public getResumoDiferenca(idCofx: number): number {
    const total = this.vlTotal.get(idCofx) ?? 0;
    const pago = this.vlPago.get(idCofx) ?? 0;
    return total - pago;
  }

  public getResumoDiferencaSaldo(idCofx: number, vlSaldo: number): number {
    const pago = this.vlPago.get(idCofx) ?? 0;
    return vlSaldo - pago;
  }

  public getResumoAccent(cofx: BeControleFinancaXMovimento): string {
    return this.getResumoAccentByNome(cofx.noTipoMovimento);
  }

  public getResumoAccentByNome(noTipoMovimento?: string | null): string {
    if (!noTipoMovimento)
      return '#3F51B5';
    if (noTipoMovimento.includes('Entrada'))
      return '#2e7d32';
    if (noTipoMovimento.includes('Saída'))
      return '#c62828';
    return '#3F51B5';
  }

  public getResumoAccentById(idCofx: number): string {
    if (idCofx === -1)
      return '#c62828';
    if (idCofx === 0)
      return '#00695c';
    return '#3F51B5';
  }

  public getResumoSpecialIcon(idCofx: number): string {
    if (idCofx === -1)
      return 'payments';
    if (idCofx === 0)
      return 'account_balance_wallet';
    return 'paid';
  }

  public isCofxTotalmentePago(idCofx: number): boolean {
    const total = this.vlTotal.get(idCofx) ?? 0;
    const pago = this.vlPago.get(idCofx) ?? 0;
    if (total <= 0)
      return false;
    return pago + 0.005 >= total;
  }

  public onCalcularValorTotal() {
    this.unsubscribe();

    this.vlCartao = 0;
    this.vlEntrada = 0;
    this.vlSaida = 0;
    this.vlTotal.clear();
    this.vlPago.clear();

    const requests = this.listaControleFinancaXMovimento.map((cofx) => {
      const idCofx = cofx.idControleFinancaXMovimento;
      return forkJoin({
        total: this.httpClient.get<number>(`/api/ControleFinancaMovimentos/VlTotalPorIdCofx/${idCofx}`),
        pago: this.httpClient.get<number>(`/api/ControleFinancaMovimentos/VlTotalPagoPorIdCofx/${idCofx}`),
      }).pipe(
        catchError(err => {
          this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
          return of({ total: 0, pago: 0 });
        })
      );
    });

    let vlPagoEntrada = 0;
    let vlPagoSaida = 0;
    let vlPagoCartao = 0;
    let vlSaidaAcumulada = 0;

    this.subscription = forkJoin(requests)
      .pipe(finalize(() => this.unsubscribe()))
      .subscribe((results) => {
        results.forEach((res, index) => {
          const cofx = this.listaControleFinancaXMovimento[index];
          const total = Number(res.total ?? 0);
          const pago = Number(res.pago ?? 0);

          this.vlTotal.set(cofx.idControleFinancaXMovimento, total);
          this.vlPago.set(cofx.idControleFinancaXMovimento, pago);

          if (cofx.noTipoMovimento == 'Entrada') {
            this.vlEntrada = (this.vlEntrada ?? 0) + total;
            vlPagoEntrada += pago;
          }
          else if (cofx.noTipoMovimento == 'Saída') {
            this.vlSaida = (this.vlSaida ?? 0) + total;
            vlPagoSaida += pago;
            vlSaidaAcumulada += total;
          }
          else if (!cofx.vlSaldo) {
            this.vlCartao += total;
            vlPagoCartao += pago;
            vlSaidaAcumulada += total;
          }
          else {
            vlSaidaAcumulada += total;
          }
        });

        const totalSaidaCartao = (this.vlSaida ?? 0) + (this.vlCartao ?? 0);
        const totalPagoSaidaCartao = vlPagoSaida + vlPagoCartao;
        const totalSaldo = (this.vlEntrada ?? 0) - (this.vlSaida ?? 0) - (this.vlCartao ?? 0);
        const totalPagoSaldo = vlPagoEntrada - totalPagoSaidaCartao;

        this.vlTotal.set(-1, totalSaidaCartao);
        this.vlPago.set(-1, totalPagoSaidaCartao);
        this.vlTotal.set(0, totalSaldo);
        this.vlPago.set(0, totalPagoSaldo);

        const vlAcumuladoAnterior = Number(this.vlAcumuladoMesAnterior ?? 0);
        this.vlSaldoAcumulado = vlAcumuladoAnterior + (this.vlEntrada ?? 0) - vlSaidaAcumulada;
        this.vlSaldoAtual = vlAcumuladoAnterior + this.getResumoPago(0);
      });
  }

  public onExcluirCartao(res: BeControleFinancaXMovimento) {
    this.listaControleFinancaXMovimentoCartoes.splice(this.listaControleFinancaXMovimentoCartoes.indexOf(res), 1);
    this.listaControleFinancaXMovimento.splice(this.listaControleFinancaXMovimento.indexOf(res), 1);
    this.onCalcularValorTotal();
    this.snackBarService.exibirSucesso('Cartão excluído!');
  }

  public onDefinirCartao(movimento: BeControleFinancaMovimento) {
    const child = this.childComponents.find(c => c.controleFinancaXMovimento?.idControleFinancaXMovimento === movimento.idControleFinancaXMovimento);
    if (child) {
      child.dataSrc.data.push(movimento);
      child.dataSrc._updateChangeSubscription();
    }
  }

  public onTabIndexChange(index: number) {
    const scrollY = window.scrollY;
    this.selectedIndex = index;

    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
  }

  public onMovimentosCountChanged(idCofx: number, count: number): void {
    this.movimentosCount.set(idCofx, count);
  }

  public getMovimentosCountFor(lista?: BeControleFinancaXMovimento[] | null): number {
    return (lista ?? []).reduce((acc, cofx) => {
      const id = cofx.idControleFinancaXMovimento;
      return acc + (this.movimentosCount.get(id) ?? 0);
    }, 0);
  }

  public getMovimentosCount(idCofx: number): number {
    return this.movimentosCount.get(idCofx) ?? 0;
  }

  public onPreviousData() {
    if (this.idControleFinancaAnterior)
      this.onChangeData();
  }

  public onNextData() {
    if (this.idControleFinancaProximo)
      this.onChangeData(true);
  }

  public getIconColor(cofx: BeControleFinancaXMovimento): { icon: string; color: string } {
    let icon = { icon: 'credit_card', color: '' };

    if (cofx.noTipoMovimento === 'Entrada') {
      icon.color = '#008000';
      icon.icon = 'paid';
    }

    if (cofx.noTipoMovimento === 'Saída') {
      icon.color = '#800000';
      icon.icon = 'paid';
    }

    return icon;
  }

  private onCarregarCampos() {
    const idControleFinanca = Number(this.route.snapshot.paramMap.get('id'));
    if (idControleFinanca) {
      this.subscription = forkJoin([
        this.httpClient.get<BeControleFinanca>(`/api/ControleFinancas/${idControleFinanca}`),
        this.httpClient.get<BeControleFinancaXMovimento[]>(`/api/ControleFinancaXMovimentos/ListarPorIdCofi/${idControleFinanca}`)
      ]).subscribe({
        next: (res) => {
          this.editIdControleFinancaFormControl.setValue(res[0].idControleFinanca!);
          this.editIdUsuarioFormControl.setValue(res[0].idUsuario!);
          this.editDtControleFormControl.setValue(res[0].dtControle);
          this.idControleFinancaAnterior = res[0].idControleFinancaAnterior;
          this.idControleFinancaProximo = res[0].idControleFinancaProximo;
          this.vlAcumuladoMesAnterior = res[0].vlAcumuladoMesAnterior;
          this.blMesFinalizado = res[0].blMesFinalizado;
          this.vlSaldoAcumulado = res[0].vlSaldoAcumulado;
          this.blMesAnteriorFinalizado = res[0].blMesAnteriorFinalizado;
          this.blMesProximoFinalizado = res[0].blMesProximoFinalizado;
          this.listaControleFinancaXMovimento = res[1];
          this.controleFinancaXMovimentoEntrada = res[1].filter(a => a.noTipoMovimento.includes('Entrada'));
          this.controleFinancaXMovimentoSaida = res[1].filter(a => a.noTipoMovimento.includes('Saída'));
          this.listaControleFinancaXMovimentoCartoes = this.listaControleFinancaXMovimento.filter(a => !a.noTipoMovimento.includes('Entrada') && !a.noTipoMovimento.includes('Saída'));
          this.unsubscribe();
          this.onCalcularValorTotal();
        },
        error: (err) => {
          this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
          this.router.navigate(['/controle-financas'], { relativeTo: this.route });
          this.unsubscribe();
        }
      });
    }
  }

  private onChangeData(next: boolean = false) {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([`controle-financa/${next ? this.idControleFinancaProximo : this.idControleFinancaAnterior}`]);
    });
  }

  private unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }
}
